import type { Phase, WorkItem } from './workflow'

// Actions a user can attempt on a work item. The lifecycle use-cases route
// through canActor so real role/department/team rules can be filled in later
// without changing call sites.
type WorkItemAction =
	| 'assign'
	| 'complete'
	| 'cancel'
	| 'archive'
	| 'reopen'
	| 'comment'
	| 'reassign_workflow'
	| 'accept_handoff'
	| 'reject_handoff'
	| 'fire_transition'
	| 'conclude_spawn'
	| 'set_work_state'

// Minimal actor context. Expand with department/team/permission data when the
// permission layer is wired; the shape can grow without touching call sites.
type ActorContext = {
	actorId: string
	actorName: string
	companyRole?: string
	teamIds?: string[]
}

// Company-level roles with full board visibility: they see every phase. Other
// members see only the phases owned by a team they belong to.
const MANAGEMENT_ROLES = ['owner', 'manager']

const isManagement = (ctx: ActorContext): boolean =>
	ctx.companyRole !== undefined && MANAGEMENT_ROLES.includes(ctx.companyRole)

// Returns true when the actor belongs to the given team.
const isTeamMember = (ctx: ActorContext, teamId: string): boolean =>
	(ctx.teamIds ?? []).includes(teamId)

// Resolve which team owns the phase the item currently lives in.
// Returns null when the phase cannot be found (e.g. incomplete workflow data).
const resolveItemPhaseTeamId = (phases: Phase[], item: WorkItem): string | null =>
	phases.find(p => p.id === item.currentPhaseId)?.teamId ?? null

// Whether the actor is either management or a member of the item's current phase team.
const canActOnPhase = (ctx: ActorContext, phaseTeamId: string | null): boolean =>
	isManagement(ctx) || (phaseTeamId !== null && isTeamMember(ctx, phaseTeamId))

// Authorization rules per action.
//
// Management (owner / manager) can do everything.
// Members are scoped to tasks in their team's phases.
//
// assign           - management, or any member of the phase team (self-assign or delegate within team)
// fire_transition  - management, assignee, or any phase-team member
// set_work_state   - same as fire_transition (it's the in_progress toggle)
// comment          - anyone who can see the item: management or phase-team member
// complete         - management, or the assignee (the person doing the work confirms it)
// cancel           - management, or the assignee
// reopen           - management only (elevated; spec says "requires elevated permission")
// archive          - management only (force-archive cascades; needs elevated)
// reassign_workflow- management only (spec: "requires approval from a manager or above")
// accept_handoff   - any member of the receiving phase team (symmetric to reject)
// reject_handoff   - any member of the receiving phase team (with reason, checked in use-case)
// conclude_spawn   - lightweight: anyone in the phase; blocking/async: management or assignee
//
// When phaseTeamId is null and the actor has no role/teams (system / unauthenticated dev actor),
// fall back to permissive - mirrors the visiblePhases fallback for the same unauthenticated case.
const canActor = (action: WorkItemAction, item: WorkItem, ctx: ActorContext, phaseTeamId: string | null = null): boolean => {
	if (isManagement(ctx)) return true

	// Unauthenticated / system actor: no role, no teams, no phase context → permissive fallback
	const hasRoleInfo = ctx.companyRole !== undefined || (ctx.teamIds !== undefined && ctx.teamIds.length > 0)
	if (!hasRoleInfo && phaseTeamId === null) return true

	const inPhaseTeam = phaseTeamId !== null && isTeamMember(ctx, phaseTeamId)
	const isAssignee = item.assignedTo !== null && item.assignedTo === ctx.actorId

	switch (action) {
		case 'assign':
			return inPhaseTeam
		case 'fire_transition':
		case 'set_work_state':
		case 'comment':
			return inPhaseTeam || isAssignee
		case 'complete':
		case 'cancel':
			return isAssignee
		case 'reopen':
		case 'archive':
		case 'reassign_workflow':
			return false
		case 'conclude_spawn':
			if (item.lightweight) return inPhaseTeam || isAssignee
			return isAssignee
		case 'accept_handoff':
		case 'reject_handoff':
			return inPhaseTeam
	}
}

// The phases a viewer can see on the board. Management sees all; everyone else
// sees only phases their team owns. Order is preserved. When no scoping info is
// present (no role and no teams, e.g. an unauthenticated/system actor), fall back
// to showing all phases rather than rendering an empty board.
const visiblePhases = (phases: Phase[], ctx: ActorContext): Phase[] => {
	if (isManagement(ctx)) return phases
	if (ctx.companyRole === undefined && (ctx.teamIds === undefined || ctx.teamIds.length === 0)) return phases
	const teamIds = new Set(ctx.teamIds ?? [])
	return phases.filter(phase => teamIds.has(phase.teamId))
}

export type { WorkItemAction, ActorContext }
export { canActor, isManagement, isTeamMember, resolveItemPhaseTeamId, canActOnPhase, visiblePhases }
